import React from "react";
import { IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import PrintIcon from "@mui/icons-material/Print";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import HighlightOff from "@mui/icons-material/HighlightOff";
import LockOpen from "@mui/icons-material/LockOpen";

import { COLORS } from "../../Pages/admin/shared/tokens";

/*
 * The ONE way a grid row shows its actions (2026-10-22).
 *
 * Before this, the 36 grid screens used five different styles — a contained "Edit" button, a red
 * icon-only contained button for delete, a bare padding-0 IconButton, a contained "View", a
 * contained "PRINT" — each wrapped differently. Every row action now goes through here, so they
 * all look and behave the same.
 *
 *   <RowActions actions={[
 *     { type: 'edit',   onClick: () => this.handleEdit(idx) },
 *     { type: 'delete', onClick: () => this.handleDelete(id), show: canDelete },
 *   ]} />
 *
 * action = {
 *   type:     'edit' | 'view' | 'delete' | 'print'
 *             | 'request' | 'approve' | 'reject' | 'reopen'   (approval transitions — the same
 *             names helper.js approvalActions() returns as `type`)
 *             Picks the icon, the colour and the default label.
 *   onClick:  the screen's own handler, unchanged
 *   title:    optional tooltip text, overriding the default label
 *   show:     optional, default true — false renders nothing, so a screen keeps its existing
 *             visibility rule exactly
 *   disabled: optional
 * }
 *
 * The tooltip text doubles as the aria-label, since an icon-only button has no visible name.
 */
const TYPES = {
  edit:    { label: "Edit",              Icon: EditIcon },
  view:    { label: "View",              Icon: VisibilityIcon },
  delete:  { label: "Delete",            Icon: DeleteOutline,      tone: 'error' },
  print:   { label: "Print",             Icon: PrintIcon },
  request: { label: "Send for approval", Icon: SendIcon },
  approve: { label: "Approve",           Icon: CheckCircleOutline, tone: 'success' },
  reject:  { label: "Reject",            Icon: HighlightOff,       tone: 'error' },
  reopen:  { label: "Reopen",            Icon: LockOpen,           tone: 'warning' },
};

// Icon colour and hover fill per tone — all from shared/tokens.js, nothing hardcoded.
const TONES = {
  primary: { fg: COLORS.primary,     bg: COLORS.primaryTint },
  error:   { fg: COLORS.errorText,   bg: COLORS.errorBg },
  success: { fg: COLORS.successText, bg: COLORS.successBg },
  warning: { fg: COLORS.warningText, bg: COLORS.warningBg },
};

export default function RowActions({ actions = [] }) {
  const visible = actions.filter(a => a && a.show !== false && TYPES[a.type]);
  if (visible.length === 0) return null;

  return (
    // nowrap: the grid lets cells wrap, but a row's buttons must stay on one line
    <span style={{ display: "inline-flex", gap: "6px", flexWrap: "nowrap", verticalAlign: "middle" }}>
      {visible.map((a, i) => {
        const { label, Icon, tone } = TYPES[a.type];
        const text = a.title || label;
        const t = TONES[tone || 'primary'];
        return (
          <Tooltip key={i} title={text} arrow>
            {/* span keeps the tooltip working when the button is disabled */}
            <span>
              <IconButton
                size="small"
                aria-label={text}
                disabled={!!a.disabled}
                onClick={a.onClick}
                sx={{
                  width: 32, height: 32,
                  borderRadius: "8px",
                  border: `1px solid ${COLORS.border}`,
                  background: COLORS.surface,
                  color: t.fg,
                  "&:hover": { background: t.bg, borderColor: t.fg },
                  "& .MuiSvgIcon-root": { fontSize: "18px" },
                }}
              >
                <Icon />
              </IconButton>
            </span>
          </Tooltip>
        );
      })}
    </span>
  );
}
